import { useState, useEffect } from "react";
import { changePasswordApi, changeUsernameApi, useEmailConfig, useUpdateEmailConfig, useSendTestEmail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Plus, X, Mail, Send, Loader2, Phone } from "lucide-react";

function EmailConfigSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: emailConfig, isLoading } = useEmailConfig();
  const updateConfig = useUpdateEmailConfig();
  const sendTestEmail = useSendTestEmail();
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [notifyAdmin, setNotifyAdmin] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (emailConfig) {
      setEmails(emailConfig.notificationEmails || []);
      setNotifyAdmin(emailConfig.notifyAdminOnOrder);
      setNotifyCustomer(emailConfig.notifyCustomerOnOrder);
      setWhatsappNumber(emailConfig.whatsappNumber || "");
    }
  }, [emailConfig]);

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Por favor ingresa una dirección de correo válida.", variant: "destructive" });
      return;
    }
    if (emails.includes(trimmed)) {
      toast({ title: "Este correo ya fue agregado.", variant: "destructive" });
      return;
    }
    setEmails(prev => [...prev, trimmed]);
    setNewEmail("");
    setHasChanges(true);
  };

  const removeEmail = (email) => {
    setEmails(prev => prev.filter(e => e !== email));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({
        notificationEmails: emails,
        notifyAdminOnOrder: notifyAdmin,
        notifyCustomerOnOrder: notifyCustomer,
        whatsappNumber: whatsappNumber.trim() || null,
      });
      queryClient.invalidateQueries({ queryKey: ["email-config"] });
      toast({ title: "Configuración de correo guardada." });
      setHasChanges(false);
    } catch {
      toast({ title: "Error al guardar la configuración de correo.", variant: "destructive" });
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) return;
    try {
      await sendTestEmail.mutateAsync(testEmail.trim());
      toast({ title: `Correo de prueba enviado a ${testEmail}` });
      setTestEmail("");
    } catch (err) {
      toast({ title: err.response?.data?.detail || "Error al enviar el correo de prueba.", variant: "destructive" });
    }
  };

  if (isLoading) return <div className="py-4 text-center text-xs text-muted-foreground">Cargando configuración de correo...</div>;

  return (
    <div className="space-y-5">
      <h3 className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" /> Notificaciones por Correo
      </h3>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs font-medium">Notificar al administrador en nuevo pedido</Label>
            <p className="text-[10px] text-muted-foreground">Enviar correo al administrador cuando un cliente realice un pedido</p>
          </div>
          <Switch checked={notifyAdmin} onCheckedChange={(v) => { setNotifyAdmin(v); setHasChanges(true); }} data-testid="toggle-notify-admin" />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs font-medium">Notificar al cliente en pedido</Label>
            <p className="text-[10px] text-muted-foreground">Enviar correo de confirmación del pedido al cliente</p>
          </div>
          <Switch checked={notifyCustomer} onCheckedChange={(v) => { setNotifyCustomer(v); setHasChanges(true); }} data-testid="toggle-notify-customer" />
        </div>
      </div>

      <Separator />

      <div>
        <Label className="text-[10px] uppercase tracking-widest mb-2 block">Correos de notificación del administrador</Label>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="admin@ejemplo.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
            className="rounded-none flex-1"
            data-testid="email-config-input"
          />
          <Button type="button" variant="outline" size="sm" className="rounded-none h-10 px-3" onClick={addEmail} data-testid="add-email-btn">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        {emails.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {emails.map((email) => (
              <div key={email} className="flex items-center justify-between bg-muted/50 px-3 py-2 text-sm" data-testid={`email-tag-${email}`}>
                <span className="text-xs font-mono truncate">{email}</span>
                <button onClick={() => removeEmail(email)} className="text-muted-foreground hover:text-destructive ml-2 shrink-0" data-testid={`remove-email-${email}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {emails.length === 0 && (
          <p className="text-[10px] text-muted-foreground mt-2">No hay correos de administrador configurados. Agrega al menos uno para recibir notificaciones de pedidos.</p>
        )}
      </div>

      <Separator />

      <div>
        <h3 className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2 mb-3">
          <Phone className="w-3.5 h-3.5" /> Botón de WhatsApp
        </h3>
        <p className="text-[10px] text-muted-foreground mb-3">Aparecerá un botón flotante de WhatsApp en tu sitio cuando esté configurado. Los visitantes podrán contactarte directamente.</p>
        <Label className="text-[10px] uppercase tracking-widest mb-2 block">Número de WhatsApp (con código de país)</Label>
        <Input
          type="tel"
          placeholder="+57 300 1234567"
          value={whatsappNumber}
          onChange={(e) => { setWhatsappNumber(e.target.value); setHasChanges(true); }}
          className="rounded-none"
          data-testid="whatsapp-number-input"
        />
        <p className="text-[10px] text-muted-foreground mt-1.5">Incluye el código de país. Ejemplo: +57 para Colombia, +1 para EE.UU.</p>
      </div>

      <Button
        type="button"
        size="sm"
        className="rounded-none uppercase tracking-widest text-[10px] w-full"
        onClick={handleSave}
        disabled={updateConfig.isPending || !hasChanges}
        data-testid="save-email-config-btn"
      >
        {updateConfig.isPending ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Guardando...</> : "Guardar Configuración"}
      </Button>

      <Separator />

      <div>
        <Label className="text-[10px] uppercase tracking-widest mb-2 block">Enviar correo de prueba</Label>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="prueba@ejemplo.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="rounded-none flex-1"
            data-testid="test-email-input"
          />
          <Button type="button" variant="outline" size="sm" className="rounded-none h-10 px-3" onClick={handleTestEmail} disabled={sendTestEmail.isPending || !testEmail.trim()} data-testid="send-test-email-btn">
            {sendTestEmail.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SettingsDialog({ isOpen, onOpenChange, currentUser, onUserUpdated }) {
  const { toast } = useToast();
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [unForm, setUnForm] = useState({ newUsername: "", password: "" });
  const [pwError, setPwError] = useState("");
  const [unError, setUnError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [unLoading, setUnLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (pwForm.newPassword !== pwForm.confirmPassword) { setPwError("Las contraseñas no coinciden"); return; }
    if (pwForm.newPassword.length < 6) { setPwError("Mínimo 6 caracteres"); return; }
    setPwLoading(true);
    try {
      await changePasswordApi({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast({ title: "Contraseña actualizada exitosamente." });
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPwError(err.response?.data?.detail || "Error al actualizar la contraseña");
    } finally { setPwLoading(false); }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    setUnError("");
    if (!unForm.newUsername.trim()) { setUnError("El nombre de usuario es obligatorio"); return; }
    setUnLoading(true);
    try {
      const data = await changeUsernameApi({ newUsername: unForm.newUsername, password: unForm.password });
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_user", JSON.stringify(data.user));
      toast({ title: "Nombre de usuario actualizado exitosamente." });
      setUnForm({ newUsername: "", password: "" });
      if (onUserUpdated) onUserUpdated(data.user);
    } catch (err) {
      setUnError(err.response?.data?.detail || "Error al actualizar el nombre de usuario");
    } finally { setUnLoading(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none p-8 max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader className="mb-6">
          <DialogTitle className="font-serif text-2xl">Configuración de Cuenta</DialogTitle>
        </DialogHeader>

        {/* Configuración de notificaciones por correo */}
        <EmailConfigSection />

        <Separator className="my-6" />

        {/* Cambiar contraseña */}
        <form onSubmit={handleChangePassword} className="space-y-4">
          <h3 className="text-xs uppercase tracking-widest font-semibold">Cambiar Contraseña</h3>
          {pwError && <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 p-2"><AlertCircle className="w-3 h-3" />{pwError}</div>}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest">Contraseña Actual</Label>
            <Input type="password" value={pwForm.currentPassword} onChange={e => setPwForm(p => ({...p, currentPassword: e.target.value}))} className="rounded-none" required data-testid="current-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest">Nueva Contraseña</Label>
            <Input type="password" value={pwForm.newPassword} onChange={e => setPwForm(p => ({...p, newPassword: e.target.value}))} className="rounded-none" required data-testid="new-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest">Confirmar Nueva Contraseña</Label>
            <Input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({...p, confirmPassword: e.target.value}))} className="rounded-none" required data-testid="confirm-password" />
          </div>
          <Button type="submit" size="sm" className="rounded-none uppercase tracking-widest text-[10px]" disabled={pwLoading} data-testid="save-password-btn">
            {pwLoading ? "Guardando..." : "Actualizar Contraseña"}
          </Button>
        </form>

        <Separator className="my-6" />

        {/* Cambiar nombre de usuario */}
        <form onSubmit={handleChangeUsername} className="space-y-4">
          <h3 className="text-xs uppercase tracking-widest font-semibold">Cambiar Nombre de Usuario</h3>
          <p className="text-xs text-muted-foreground">Actual: <strong>{currentUser?.username}</strong></p>
          {unError && <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 p-2"><AlertCircle className="w-3 h-3" />{unError}</div>}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest">Nuevo Nombre de Usuario</Label>
            <Input value={unForm.newUsername} onChange={e => setUnForm(p => ({...p, newUsername: e.target.value}))} className="rounded-none" required data-testid="new-username" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-widest">Confirmar Contraseña</Label>
            <Input type="password" value={unForm.password} onChange={e => setUnForm(p => ({...p, password: e.target.value}))} className="rounded-none" required data-testid="confirm-username-password" />
          </div>
          <Button type="submit" size="sm" className="rounded-none uppercase tracking-widest text-[10px]" disabled={unLoading} data-testid="save-username-btn">
            {unLoading ? "Guardando..." : "Actualizar Nombre de Usuario"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}